import * as React from "react"
import {
  type WorkItem,
  WorkItemErrorType,
  WorkItemPushFieldType,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgInfoCircle } from "@digits-shared/components/SVGIcons/line/InfoCircle.svg"
import { SvgLinkExternal01 } from "@digits-shared/components/SVGIcons/line/LinkExternal01.svg"
import {
  HoverPopover,
  HoverPopoverContent,
  HoverPopoverTrigger,
} from "@digits-shared/DesignSystem/Popovers/HoverPopover"
import colors from "@digits-shared/themes/colors"
import { BodyText, LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { useWorkItemPush } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/hooks/useWorkItemPush"
import { type WorkItemRowProps } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Shared/WorkItemRowSharedElements"
import { quickbooksTransactionLink } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemDetails/WorkItemError"
import { useSupportingTransactions } from "src/frontend/components/OS/Shared/WorkItems/useSupportingTransactions"
import { FrontendPartyRole } from "src/frontend/types/FrontendPartyRole"

/*
  STYLES
*/

const ErrorTitle = styled(LabelText)`
  position: absolute;
  bottom: -14px;
  left: 0;
  color: ${colors.orange};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const ExternalLink = styled.a`
  color: ${colors.primaryDark};
  text-decoration: underline;
  margin-right: 4px;

  &:hover {
    text-shadow: ${colors.accentPurple};
    cursor: pointer;
  }
`

const OpenLinkIcon = styled(SvgLinkExternal01)`
  ${svgPathStyles(colors.primaryDark, 1.5)};
  height: 12px;
  width: 12px;
  margin-left: 4px;
`

const InfoIcon = styled(SvgInfoCircle)`
  ${svgPathStyles(colors.orange, 1.5)};
  width: 10px;
  height: 10px;
`

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

/*
  COMPONENT
*/

export const WorkItemErrorPopover: React.FC<
  WorkItemRowProps & { field: WorkItemPushFieldType }
> = ({ workItem, field }) => {
  const { errorType } = useWorkItemPush(workItem, field)

  switch (errorType) {
    case WorkItemErrorType.NotSupported:
      return <WorkItemNotSupportedError workItem={workItem} field={field} />

    case WorkItemErrorType.PeriodClosed:
      return <WorkItemPeriodClosedError workItem={workItem} />

    case WorkItemErrorType.NoError:
      return null

    case WorkItemErrorType.Other:
    default:
      return <WorkItemDefaultError />
  }
}

const WorkItemNotSupportedError: React.FC<WorkItemRowProps & { field: WorkItemPushFieldType }> = ({
  workItem,
  field,
}) => {
  const fieldText = useFieldDisplayText(workItem, field)
  return (
    <ErrorTooltipContainer>
      <BodyText>
        Updating the {fieldText} for this
        <br /> transaction type is not supported.
      </BodyText>
      <BodyText>
        The update can be <QBOWorkItemLink workItem={workItem} linkText="made in QBO" />
      </BodyText>
      <LabelText>Ensure you are logged into the QBO account for this client.</LabelText>
    </ErrorTooltipContainer>
  )
}

const WorkItemPeriodClosedError: React.FC<WorkItemRowProps> = ({ workItem }) => (
  <ErrorTooltipContainer>
    <BodyText>
      The accounting period for this transaction <br /> is closed.
    </BodyText>
    <BodyText>
      Updates can be made once the
      <br /> period <QBOWorkItemLink workItem={workItem} linkText="is reopened in QBO" />
    </BodyText>

    <LabelText>Ensure you are logged into the QBO account for this client.</LabelText>
  </ErrorTooltipContainer>
)

const WorkItemDefaultError: React.FC = () => (
  <ErrorTooltipContainer>
    <BodyText>Updating this transaction is not supported.</BodyText>
    <BodyText>
      <ExternalLink href="mailto:support@digits.com" rel="noreferrer">
        Contact Support
      </ExternalLink>
      for more details.
    </BodyText>
  </ErrorTooltipContainer>
)

const ErrorTooltipContainer: React.FC<React.PropsWithChildren> = ({ children }) => (
  <HoverPopover>
    <HoverPopoverTrigger asChild>
      <ErrorTitle>
        QBO Push Error <InfoIcon />
      </ErrorTitle>
    </HoverPopoverTrigger>
    <HoverPopoverContent>
      <ErrorContent>{children}</ErrorContent>
    </HoverPopoverContent>
  </HoverPopover>
)

const QBOWorkItemLink = React.forwardRef<HTMLDivElement, WorkItemRowProps & { linkText: string }>(
  ({ workItem, linkText }, ref) => {
    const { object: transaction } = useSupportingTransactions(workItem)
    const quickbooksAppName = transaction?.quickbooksAppName
    const externalId = transaction?.externalTransactionId

    if (!quickbooksAppName || !externalId) return <>{linkText}</>

    return (
      <ExternalLink
        href={quickbooksTransactionLink(quickbooksAppName, externalId)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkText}
        <OpenLinkIcon />
      </ExternalLink>
    )
  }
)

function useFieldDisplayText(workItem: WorkItem, field: WorkItemPushFieldType) {
  const { object: transaction } = useSupportingTransactions(workItem)

  switch (field) {
    case WorkItemPushFieldType.Category:
      return "category"
    case WorkItemPushFieldType.Party: {
      let label = "vendor"
      const role = FrontendPartyRole.findByRole(transaction?.displayPartyRole)
      if (role !== FrontendPartyRole.Unknown) {
        label = role.displayName.toLowerCase()
      }
      return label
    }
    default:
      return "field"
  }
}
